import { DateTime } from 'luxon';
import { toLocaleString } from '../utils/format';
import { useBudgetLocale } from '../budget/budget-store';
import { useTimeZone } from '../user/api/use-get-user';
/**
 * Cache for storing formatted date results to avoid re-computation.
 * Key format: "{isoDate}|{locale}|{timeZone}" (e.g., "2024-03-15T10:30:00Z|en-US|America/New_York")
 */
const formattedDateCache = new Map();
/**
 * Creates a cache key for the formatted date result.
 *
 * @param isoDate - The ISO date string
 * @param locale - The locale string (e.g., "en-US")
 * @param timeZone - The IANA timezone identifier (e.g., "America/New_York")
 * @returns A unique cache key combining all formatting parameters
 */
const createCacheKey = (isoDate, locale, timeZone) => {
    return `${isoDate}|${locale}|${timeZone}`;
};
/**
 * Custom React hook that provides a date formatter function with result caching.
 *
 * This hook creates a date formatter that converts ISO date strings to localized
 * date format based on the user's budget locale and timezone. Month and day values
 * are zero-padded to ensure consistent formatting. The formatted results are cached
 * globally across the entire application to improve performance when the same date
 * needs to be formatted multiple times.
 *
 * The cache persists across component re-renders and re-mounts, ensuring that
 * identical formatting operations return cached results without re-computation.
 *
 * @returns {DateFormatter} A function that formats ISO date strings to localized
 *   date format. Returns empty string for null/undefined input or invalid dates.
 *   Month and day are zero-padded for consistent alignment.
 *
 * @example
 * ```typescript
 * const formatDate = useDateFormatter();
 * const formattedDate = formatDate('2024-03-05T10:30:00Z'); // "03/05/2024" (for US locale)
 * const emptyDate = formatDate(null); // ""
 * const invalidDate = formatDate('invalid-date'); // "" (logs error)
 * ```
 *
 * @see {@link DateFormatter} for the function signature
 */
export function useDateFormatter() {
    const locale = useBudgetLocale();
    const timeZone = useTimeZone();
    const localeString = toLocaleString(locale);
    return (isoDate) => {
        if (!isoDate) {
            return '';
        }
        const cacheKey = createCacheKey(isoDate, localeString, timeZone);
        // return cached result if available
        if (formattedDateCache.has(cacheKey)) {
            return formattedDateCache.get(cacheKey);
        }
        const dateTime = DateTime.fromISO(isoDate).setZone(timeZone);
        if (!dateTime.isValid) {
            console.error(`Invalid date string: ${isoDate}`);
            return '';
        }
        const formatter = new Intl.DateTimeFormat(localeString, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            timeZone: timeZone,
        });
        const formattedResult = formatter.format(dateTime.toJSDate());
        // cache the result for future use
        formattedDateCache.set(cacheKey, formattedResult);
        return formattedResult;
    };
}
